import { CONTROL_BUDGET_MODEL, parseBudgetModelPayload } from '@timelog/pm-shared';
import type { BudgetModelConfig, BudgetModelSource } from '@timelog/pm-shared';

/**
 * Resolution of the `project-budget-model` multivariate flag (ADR 0027).
 *
 * Resolved ONCE per page load, before Financials first renders; a variant
 * change in PostHog takes effect on reload, never mid-session. Every failure
 * path — no client, timeout, boolean/absent flag, unparsable payload — lands
 * on the control defaults, and says which one it was.
 */

export const BUDGET_MODEL_FLAG = 'project-budget-model';
const FLAG_TIMEOUT_MS = 1500;

/**
 * Why a session runs the control defaults instead of an assigned variant.
 *
 * The fallback itself is deliberate and silent by design — but a silent
 * fallback in a customer sandbox turns the session into a different setup with
 * nothing recording that it happened. Naming the path lets the facilitator see
 * it; it never changes which config applies.
 */
export type BudgetModelFallbackReason =
  /** No PostHog key — posthog-js was never initialised. */
  | 'no-client'
  /** Flags did not load inside the timeout. */
  | 'timeout'
  /** The flag is absent or boolean, never a variant key. */
  | 'no-variant'
  /** A variant resolved, but its payload failed `BudgetModelConfigSchema`. */
  | 'unparsable';

export type ResolvedBudgetModel = {
  config: BudgetModelConfig;
  /** Stamped on Financials analytics events as `budget_model_variant`. */
  variantKey: string;
  source: BudgetModelSource;
  /** Set only when `source` is 'control' — which failure path landed here. */
  reason?: BudgetModelFallbackReason;
};

export const CONTROL_RESOLUTION: ResolvedBudgetModel = {
  config: CONTROL_BUDGET_MODEL,
  variantKey: 'control',
  source: 'control',
};

/** The control defaults, carrying the path that fell back to them. */
export const controlResolution = (reason: BudgetModelFallbackReason): ResolvedBudgetModel => ({
  ...CONTROL_RESOLUTION,
  reason,
});

/** The slice of the PostHog client the resolver needs — injectable in tests. */
export type FlagClient = {
  onFeatureFlags: (callback: () => void) => void;
  getFeatureFlag: (key: string) => string | boolean | undefined;
  getFeatureFlagPayload: (key: string) => unknown;
};

export const resolveBudgetModelFrom = async (
  client: FlagClient | null,
  timeoutMs: number = FLAG_TIMEOUT_MS,
): Promise<ResolvedBudgetModel> => {
  if (!client) return controlResolution('no-client');

  const flagsLoaded = new Promise<'loaded'>((resolve) => {
    client.onFeatureFlags(() => resolve('loaded'));
  });
  const timedOut = new Promise<'timeout'>((resolve) => {
    setTimeout(() => resolve('timeout'), timeoutMs);
  });
  if ((await Promise.race([flagsLoaded, timedOut])) === 'timeout') {
    return controlResolution('timeout');
  }

  const variant = client.getFeatureFlag(BUDGET_MODEL_FLAG);
  if (typeof variant !== 'string') return controlResolution('no-variant');

  const { config, source } = parseBudgetModelPayload(
    client.getFeatureFlagPayload(BUDGET_MODEL_FLAG),
  );
  // A variant whose payload does not parse is a misconfigured flag — fall back
  // whole (config AND stamp), so segmentation never claims a variant that did
  // not actually apply.
  return source === 'flag'
    ? { config, variantKey: variant, source }
    : controlResolution('unparsable');
};
